import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  Input,
  Label,
  // FormFeedback,
} from "reactstrap";

import Select from "react-select";
import CustomButton from "Components/Custom/CustomButton";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { useFormik } from "formik";
import * as Yup from "yup";
import { SubCategoryType } from "pages/Settings/Cards/SubCategories";
import { getCategories, manageSubCategory } from "slices/thunks";
import { useDebounce } from "Components/Hooks/useDebounce";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  createNameValidation,
} from "Components/constants/validation";

interface AddSubCategoryModalProps {
  show: boolean;
  onCloseClick: () => void;
}

const AddSubCategoryModal: React.FC<AddSubCategoryModalProps> = ({
  show,
  onCloseClick,
}) => {
  const [searchCategory, setSearchCategory] = useState("");
  const debounceSearch = useDebounce(searchCategory, 500);
  const dispatch = useAppDispatch();

  const { categories, isLoadingCategory } = useAppSelector(
    (stat) => stat.category
  );

  const handleClose = () => {
    if (
      validation.errors.category ||
      validation.errors.id ||
      validation.errors.subCategoryName
    ) {
      return;
    }
    // onCloseClick();
  };

  const handleForceClose = () => {
    validation.resetForm();
    onCloseClick();
  };

  const validation = useFormik<SubCategoryType>({
    enableReinitialize: true,
    initialValues: {
      category: undefined,
      subCategoryName: "",
    },
    validationSchema: Yup.object({
      category: Yup.string().required(VALIDATION_MESSAGES.REQUIRED("category")),
      subCategoryName: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("subcategory name"))
        .min(
          VALIDATION_LIMITS.NAME.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Subcategory Name",
            VALIDATION_LIMITS.NAME.MIN
          )
        )
        .max(
          VALIDATION_LIMITS.NAME.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Subcategory Name",
            VALIDATION_LIMITS.NAME.MAX
          )
        )
        .test(
          "valid-subcategory-name",
          createNameValidation("Subcategory Name").message,
          createNameValidation("Subcategory Name").test
        ),
    }),
    onSubmit: (values) => {
      if (!values.category) {
        return;
      }
      dispatch(
        manageSubCategory({
          category_id: values.category,
          name: values.subCategoryName?.trim(),
        })
      );
      onCloseClick();
      validation.resetForm();
    },
  });

  useEffect(() => {
    if (show) {
      dispatch(getCategories({ search: searchCategory }));
    }
  }, [debounceSearch]);

  return (
    <Offcanvas
      direction="end"
      isOpen={show}
      id="offcanvasExample"
      toggle={handleForceClose}
      onClosed={() => {
        validation.resetForm();
      }}
    >
      <OffcanvasHeader className="bg-greyF5" toggle={handleForceClose}>
        Add Subcategory
      </OffcanvasHeader>
      <form
        className="h-100  d-flex flex-column justify-content-between"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
        }}
      >
        <div className="mb-3 p-3">
          <div>
            <Label
              htmlFor="customer-name"
              className="font-primary fw-normal fs-14 text-black2D d-flex"
            >
              Category
            </Label>
            <Select
              value={
                categories
                  .map((cat) => ({ label: cat.name, value: cat.id }))
                  .find((opt) => opt.value === validation.values.category) ||
                null
              }
              onChange={(selectedOption: any) => {
                validation.setFieldValue(
                  "category",
                  selectedOption?.value || ""
                );
              }}
              onInputChange={(inputValue: any) => {
                setSearchCategory(inputValue);
              }}
              options={categories.map((cat) => ({
                label: cat.name,
                value: cat.id,
              }))}
              name="category"
              className="auth-form-dropdown-control mb-3"
              placeholder="Search or select a category"
              classNamePrefix="react-select"
              isLoading={isLoadingCategory}
              loadingMessage={() => "Searching clients..."}
              noOptionsMessage={({ inputValue }: { inputValue: any }) =>
                inputValue
                  ? `No category found for "${inputValue}"`
                  : "No category available"
              }
              isClearable
            />

            {validation.touched.category && validation.errors.category && (
              <div className="text-danger fs-12 mt-0 pt-0 mt-n3 mb-1">
                {validation.errors.category}
              </div>
            )}
          </div>
          <div>
            <Label
              htmlFor="customer-name"
              className="font-primary fw-normal fs-14 text-black2D d-flex"
            >
              Subcategory Name
            </Label>
            <Input
              type="text"
              className="auth-form-control"
              id="customer-name"
              name="subCategoryName"
              placeholder="Subcategory Name"
              value={validation.values.subCategoryName}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={
                !!validation.touched.subCategoryName &&
                !!validation.errors.subCategoryName
              }
            />
            {validation.touched.subCategoryName &&
              validation.errors.subCategoryName && (
                <div className="text-danger fs-12 mt-0 pt-0 mb-1">
                  {validation.errors.subCategoryName}
                </div>
              )}
          </div>
        </div>
        <div className="offcanvas-footer border-top p-3 text-center hstack gap-2">
          <CustomButton
            type="submit"
            text="Save Subcategory"
            className="bg-primary w-100 br-7"
            onClick={handleClose}
          />
        </div>
      </form>
    </Offcanvas>
  );
};

export default AddSubCategoryModal;
